import { Check, Copy, KeyRound, MoreHorizontal, Plus, RefreshCw, Search, ShieldCheck, UserPlus, Users, X, Zap, Wrench } from "lucide-react";
import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import type { TechnicalPersonnel, TechnicalPersonnelCredential } from "./models";
import { productionGateway } from "./production-gateway";
import "./technical-personnel-admin.css";

type IssuedPin = { personId: string; personName: string; pin: string };
type TeamFilter = "ACTIVE" | "ON_DUTY" | "OFF_DUTY" | "INACTIVE";

const departmentLabel = (department: TechnicalPersonnel["department"]) => department === "ELECTRICAL" ? "Elektrik" : "Mekanik";

export function TechnicalPersonnelAdmin({refreshVersion,notify,canRevealPin=false}:{refreshVersion:number;notify:(message:string)=>void;canRevealPin?:boolean}) {
  const [items,setItems]=useState<TechnicalPersonnel[]>([]);
  const [loading,setLoading]=useState(true);
  const [busy,setBusy]=useState(false);
  const [formOpen,setFormOpen]=useState(false);
  const [name,setName]=useState("");
  const [department,setDepartment]=useState<TechnicalPersonnel["department"]>("ELECTRICAL");
  const [onDuty,setOnDuty]=useState(true);
  const [query,setQuery]=useState("");
  const [filter,setFilter]=useState<TeamFilter>("ACTIVE");
  const [issuedPin,setIssuedPin]=useState<IssuedPin|null>(null);
  const [copied,setCopied]=useState(false);
  const [confirmPinResetId,setConfirmPinResetId]=useState<string|null>(null);

  const load=useCallback(async()=>{
    setLoading(true);
    try { setItems((await productionGateway.technicalPersonnel()).items); }
    catch(error) { notify(error instanceof Error?error.message:"Teknik personel listesi yüklenemedi."); }
    finally { setLoading(false); }
  },[notify]);

  useEffect(()=>{void load();},[load,refreshVersion]);
  useEffect(()=>{
    if(!issuedPin)return;
    const timeout=window.setTimeout(()=>{setIssuedPin(null);setCopied(false);},60_000);
    return()=>window.clearTimeout(timeout);
  },[issuedPin]);

  const showIssuedPin=(result:TechnicalPersonnelCredential)=>{
    if(!result.issuedPersonnelCode){
      notify("Personel kaydedildi ancak PIN gösterilemedi. Listeden yeni PIN üretin.");
      return;
    }
    setIssuedPin({personId:result.id,personName:result.name,pin:result.issuedPersonnelCode});
    setCopied(false);
  };

  async function create(event:FormEvent){
    event.preventDefault();
    if(!canRevealPin){notify("Teknik personel oluşturma ve PIN işlemleri yalnız sistem yöneticisine açıktır.");return;}
    if(!name.trim()||busy)return;
    setBusy(true);
    try{
      const result=await productionGateway.createTechnicalPersonnel({requestId:crypto.randomUUID(),name:name.trim(),department,onDuty});
      setName(""); setDepartment("ELECTRICAL"); setOnDuty(true); setFormOpen(false); setFilter("ACTIVE");
      showIssuedPin(result);
      notify(`${result.name} için sistem 6 haneli PIN üretti.`);
      await load();
    }catch(error){notify(error instanceof Error?error.message:"Teknik personel kaydedilemedi.");}
    finally{setBusy(false);}
  }

  async function update(item:TechnicalPersonnel,input:Record<string,unknown>){
    if(busy)return;
    setBusy(true);
    try{
      const updated=await productionGateway.updateTechnicalPersonnel(item.id,input);
      setItems(previous=>previous.map(candidate=>candidate.id===updated.id?updated:candidate));
      notify("Teknik personel durumu güncellendi.");
    }catch(error){notify(error instanceof Error?error.message:"Personel durumu güncellenemedi.");}
    finally{setBusy(false);}
  }

  async function regeneratePin(item:TechnicalPersonnel){
    if(!canRevealPin){notify("PIN işlemleri yalnız sistem yöneticisine açıktır.");return;}
    if(busy)return;
    if(confirmPinResetId!==item.id){setConfirmPinResetId(item.id);return;}
    setBusy(true);
    try{
      const updated=await productionGateway.regenerateTechnicalPersonnelPin(item.id,item.updatedAt);
      setItems(previous=>previous.map(candidate=>candidate.id===updated.id?updated:candidate));
      setConfirmPinResetId(null); showIssuedPin(updated);
      notify(`${updated.name} için sistem yeni PIN üretti; eski PIN iptal edildi.`);
    }catch(error){notify(error instanceof Error?error.message:"Yeni PIN üretilemedi.");}
    finally{setBusy(false);}
  }

  async function revealPin(item:TechnicalPersonnel){
    if(busy)return;
    if(!item.pinRevealable){notify("Bu eski PIN yalnız hash olarak kayıtlı. Önce yeni PIN üretin.");return;}
    setBusy(true);
    try{
      const result=await productionGateway.revealTechnicalPersonnelPin(item.id);
      setIssuedPin({personId:result.personId,personName:result.personName,pin:result.personnelCode}); setCopied(false);
      notify(`${result.personName} PIN’i güvenli kasadan açıldı.`);
    }catch(error){notify(error instanceof Error?error.message:"PIN görüntülenemedi.");}
    finally{setBusy(false);}
  }

  async function copyPin(){
    if(!issuedPin)return;
    try{await navigator.clipboard.writeText(issuedPin.pin);setCopied(true);notify("PIN panoya kopyalandı.");}
    catch{notify("PIN kopyalanamadı. Ekrandaki kodu personele iletin.");}
  }

  const activeCount=items.filter(item=>item.active).length;
  const onDutyCount=items.filter(item=>item.active&&item.onDuty).length;
  const visibleItems=useMemo(()=>{
    const normalizedQuery=query.trim().toLocaleLowerCase("tr-TR");
    return items.filter(item=>{
      const matchesFilter=filter==="ACTIVE"?item.active:filter==="ON_DUTY"?item.active&&item.onDuty:filter==="OFF_DUTY"?item.active&&!item.onDuty:!item.active;
      const matchesQuery=!normalizedQuery||`${item.name} ${departmentLabel(item.department)}`.toLocaleLowerCase("tr-TR").includes(normalizedQuery);
      return matchesFilter&&matchesQuery;
    });
  },[filter,items,query]);

  const closeForm=()=>{setFormOpen(false);setName("");setDepartment("ELECTRICAL");setOnDuty(true);};

  return <section className="technical-personnel-admin" aria-labelledby="technical-personnel-title">
    <header className="technical-team-heading">
      <div><h2 id="technical-personnel-title">Teknik ekip</h2><p>{canRevealPin?"Vardiyadaki ekibi yönetin ve personel giriş PIN’lerine erişin.":"Vardiyadaki ekibi ve görev durumunu yönetin. PIN işlemleri sistem yöneticisine aittir."}</p></div>
      <div className="technical-team-heading-actions">
        <span aria-label={`${onDutyCount} kişi vardiyada, ${activeCount} aktif personel`}><Users aria-hidden="true"/><strong>{onDutyCount}</strong> vardiyada · {activeCount} aktif</span>
        {canRevealPin?<button type="button" aria-expanded={formOpen} onClick={()=>setFormOpen(current=>!current)}>{formOpen?<X aria-hidden="true"/>:<Plus aria-hidden="true"/>}{formOpen?"Formu kapat":"Teknik personel ekle"}</button>:null}
      </div>
    </header>

    {formOpen?<form className="technical-personnel-form" onSubmit={event=>void create(event)}>
      <header><div><UserPlus aria-hidden="true"/><span><h3>Teknik personel ekle</h3><p>Adı ve uzmanlığı girin. 6 haneli giriş PIN’ini sistem oluşturur.</p></span></div><button type="button" aria-label="Personel ekleme formunu kapat" onClick={closeForm}><X aria-hidden="true"/></button></header>
      <div className="technical-personnel-form-fields">
        <label><span>Ad soyad</span><input autoFocus value={name} maxLength={120} onChange={event=>setName(event.target.value)} autoComplete="off" placeholder="Personelin adı ve soyadı"/></label>
        <fieldset><legend>Uzmanlık</legend><div><button type="button" aria-pressed={department==="ELECTRICAL"} onClick={()=>setDepartment("ELECTRICAL")}><Zap aria-hidden="true"/><span>Elektrik</span>{department==="ELECTRICAL"?<Check className="technical-specialty-check" aria-hidden="true"/>:null}</button><button type="button" aria-pressed={department==="MECHANICAL"} onClick={()=>setDepartment("MECHANICAL")}><Wrench aria-hidden="true"/><span>Mekanik</span>{department==="MECHANICAL"?<Check className="technical-specialty-check" aria-hidden="true"/>:null}</button></div></fieldset>
        <label className="technical-personnel-check"><input type="checkbox" checked={onDuty} onChange={event=>setOnDuty(event.target.checked)}/><span><strong>Vardiyaya ekle</strong><small>Açık çağrılar bu personele de ulaşır.</small></span></label>
      </div>
      <footer><p><ShieldCheck aria-hidden="true"/>PIN güvenli kasada saklanır ve yetkili yönetici tarafından görüntülenebilir.</p><div><button type="button" onClick={closeForm}>Vazgeç</button><button className="technical-personnel-submit" type="submit" disabled={busy||!name.trim()}>{busy?"Ekleniyor…":"Personeli ekle"}</button></div></footer>
    </form>:null}

    <section className="technical-personnel-list" aria-label="Teknik ekip listesi">
      <div className="technical-team-toolbar">
        <label><Search aria-hidden="true"/><span className="sr-only">Teknik ekipte ara</span><input type="search" value={query} onChange={event=>setQuery(event.target.value)} placeholder="İsim veya uzmanlık ara"/></label>
        <div className="technical-team-filters" role="group" aria-label="Personel durumuna göre filtrele">
          <button type="button" aria-pressed={filter==="ACTIVE"} onClick={()=>setFilter("ACTIVE")}>Aktif <span>{activeCount}</span></button>
          <button type="button" aria-pressed={filter==="ON_DUTY"} onClick={()=>setFilter("ON_DUTY")}>Vardiyada <span>{onDutyCount}</span></button>
          <button type="button" aria-pressed={filter==="OFF_DUTY"} onClick={()=>setFilter("OFF_DUTY")}>Vardiya dışında <span>{Math.max(0,activeCount-onDutyCount)}</span></button>
          <button type="button" aria-pressed={filter==="INACTIVE"} onClick={()=>setFilter("INACTIVE")}>Pasif <span>{Math.max(0,items.length-activeCount)}</span></button>
        </div>
      </div>

      {loading?<p className="technical-personnel-state">Teknik ekip yükleniyor…</p>:visibleItems.length?<div className="technical-team-rows">{visibleItems.map(item=><article className={!item.active?"is-inactive":undefined} key={item.id}>
        <span className={`technical-duty-indicator ${item.active&&item.onDuty?"is-on-duty":""}`} aria-hidden="true"/>
        <div className="technical-personnel-identity"><strong>{item.name}</strong><div><span>{departmentLabel(item.department)}</span><span>{!item.active?"Hesap pasif":item.onDuty?"Vardiyada":"Vardiya dışında"}</span><span>{item.pinRevealable?"PIN hazır":"PIN yenilenmeli"}</span></div></div>
        <div className="technical-personnel-actions">
          {canRevealPin&&item.pinRevealable?<button className="is-primary" type="button" disabled={busy} onClick={()=>void revealPin(item)}><KeyRound aria-hidden="true"/>PIN’i göster</button>:null}
          {canRevealPin&&!item.pinRevealable?<button className="is-primary" type="button" disabled={busy||!item.active} onClick={()=>void regeneratePin(item)}><RefreshCw aria-hidden="true"/>PIN oluştur</button>:null}
          {item.active?<button type="button" disabled={busy} onClick={()=>void update(item,{onDuty:!item.onDuty})}>{item.onDuty?"Vardiyadan çıkar":"Vardiyaya al"}</button>:<button type="button" disabled={busy} onClick={()=>void update(item,{active:true,onDuty:false})}>Etkinleştir</button>}
          {item.active?<details><summary aria-label={`${item.name} için diğer işlemler`}><MoreHorizontal aria-hidden="true"/><span>Diğer</span></summary><div>
            {canRevealPin?<button type="button" disabled={busy||!item.active} onClick={()=>void regeneratePin(item)}><RefreshCw aria-hidden="true"/>Yeni PIN üret</button>:null}
            <button className="is-danger" type="button" disabled={busy} onClick={()=>void update(item,{active:!item.active,onDuty:item.active?false:item.onDuty})}>{item.active?"Hesabı pasifleştir":"Hesabı etkinleştir"}</button>
          </div></details>:null}
        </div>
        {issuedPin?.personId===item.id?<div className="technical-inline-pin" role="status" aria-live="polite">
          <KeyRound aria-hidden="true"/><span><small>Giriş PIN’i</small><output aria-label={`${issuedPin.personName} teknik personel PIN kodu`}>{issuedPin.pin}</output></span>
          <p>Bu kodu yalnız ilgili personele iletin. 60 saniye sonra gizlenir.</p>
          <button type="button" onClick={()=>void copyPin()}>{copied?<Check aria-hidden="true"/>:<Copy aria-hidden="true"/>}{copied?"Kopyalandı":"Kopyala"}</button>
          <button type="button" className="technical-inline-pin-dismiss" aria-label="PIN’i gizle" onClick={()=>{setIssuedPin(null);setCopied(false);}}><X aria-hidden="true"/></button>
        </div>:null}
        {confirmPinResetId===item.id?<div className="technical-pin-confirm" role="alert"><span><strong>Mevcut PIN iptal edilsin mi?</strong><small>{item.name} yalnız oluşturulacak yeni PIN ile giriş yapabilecek.</small></span><button type="button" onClick={()=>setConfirmPinResetId(null)}>Vazgeç</button><button type="button" onClick={()=>void regeneratePin(item)}>Yeni PIN üret</button></div>:null}
      </article>)}</div>:<div className="technical-personnel-state"><Users aria-hidden="true"/><strong>{query.trim()?"Aramanızla eşleşen personel yok.":filter==="ACTIVE"?"Henüz aktif teknik personel yok.":filter==="ON_DUTY"?"Şu anda vardiyada teknik personel yok.":filter==="OFF_DUTY"?"Vardiya dışında aktif personel yok.":"Pasif personel yok."}</strong>{canRevealPin&&!query.trim()&&filter==="ACTIVE"?<button type="button" onClick={()=>setFormOpen(true)}>İlk personeli ekle</button>:null}</div>}
    </section>
  </section>;
}
